/**
 * Two-factor authentication card for the Password & Security page.
 *
 * Setup is two steps on purpose: the server only turns 2FA on after the user
 * types a code from the app, so a botched scan can't lock anyone out.
 */
import { useEffect, useState } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Copy, Download } from "lucide-react";

const inputClass = "mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm";

export function TwoFactorSettings() {
  const { token, setToken, user, updateUser } = useAuth();
  const [status, setStatus] = useState(null);
  // idle | setup | codes | disable | regenerate
  const [step, setStep] = useState("idle");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [busy, setBusy] = useState(false);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const loadStatus = async () => {
    try {
      const res = await fetch(`${API}/auth/2fa/status`, { headers });
      if (res.ok) setStatus(await res.json());
    } catch {
      // Card just stays hidden
    }
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const post = async (path, body) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/2fa/${path}`, {
        method: "POST",
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Something went wrong");
        return null;
      }
      return data;
    } catch {
      toast.error("Network error");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep("idle");
    setSetup(null);
    setCode("");
    setPassword("");
  };

  const startSetup = async () => {
    const data = await post("setup");
    if (data) {
      setSetup(data);
      setStep("setup");
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    const data = await post("enable", { code });
    if (!data) return;
    // Enabling logs out every other session; keep this one going
    localStorage.setItem("token", data.token);
    setToken(data.token);
    updateUser({ ...user, two_factor_enabled: true });
    setRecoveryCodes(data.recovery_codes);
    setCode("");
    setStep("codes");
    toast.success("Two-factor authentication is on");
    loadStatus();
  };

  const disable = async (e) => {
    e.preventDefault();
    const data = await post("disable", { password, code });
    if (!data) return;
    updateUser({ ...user, two_factor_enabled: false });
    toast.success(data.message);
    reset();
    loadStatus();
  };

  const regenerate = async (e) => {
    e.preventDefault();
    const data = await post("recovery-codes", { code });
    if (!data) return;
    setRecoveryCodes(data.recovery_codes);
    setCode("");
    setStep("codes");
    loadStatus();
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Recovery codes copied");
  };

  const downloadCodes = () => {
    const text = `GottaSecond recovery codes for ${user?.email}\nEach code works once.\n\n${recoveryCodes.join("\n")}\n`;
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "gottasecond-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!status || !status.available) return null;

  const codeInput = (
    <div>
      <Label className="text-sm text-white/60">Code from your authenticator app</Label>
      <Input
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="123456"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={step === "disable" ? 11 : 6}
        className={`${inputClass} tracking-widest`}
        required
        data-testid="two-factor-settings-code"
      />
    </div>
  );

  return (
    <Card className="bg-white/[0.03] border-white/10 p-8 mt-6" data-testid="two-factor-card">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-5 h-5 text-white/60" />
        <h2 className="text-2xl font-bold">Two-Factor Authentication</h2>
      </div>

      {step === "idle" && (
        <>
          <p className="text-white/50 text-sm mb-6">
            {status.enabled
              ? `On. You'll be asked for a code from your authenticator app each time you sign in. If your phone isn't handy, you can get a code by email instead. ${status.recovery_codes_left} recovery codes left.`
              : "Add a second step to sign-in: a 6-digit code from an authenticator app like Google Authenticator, 1Password or Authy. If your phone isn't handy, you can get a code by email instead."}
          </p>
          {status.required && !status.enabled && (
            <p className="text-[#FF6B6B] text-sm mb-6">Required for admin accounts.</p>
          )}
          {status.enabled ? (
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => setStep("regenerate")}
                variant="outline"
                className="w-full border-white/20 bg-transparent text-white hover:bg-white/10 rounded-sm h-12"
              >
                Get new recovery codes
              </Button>
              {!status.required && (
                <Button
                  onClick={() => setStep("disable")}
                  variant="outline"
                  className="w-full border-white/20 bg-transparent text-white/70 hover:bg-white/10 rounded-sm h-12"
                  data-testid="two-factor-disable-btn"
                >
                  Turn off
                </Button>
              )}
            </div>
          ) : (
            <Button
              onClick={startSetup}
              disabled={busy}
              className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold h-12"
              data-testid="two-factor-enable-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Turn on two-factor authentication"}
            </Button>
          )}
        </>
      )}

      {step === "setup" && setup && (
        <form onSubmit={confirmSetup} className="space-y-5">
          <p className="text-white/50 text-sm">
            1. Scan this QR code with your authenticator app.
          </p>
          <div className="bg-white p-3 rounded-sm w-fit mx-auto">
            <img src={setup.qr_code} alt="QR code for your authenticator app" className="w-48 h-48" />
          </div>
          <p className="text-white/40 text-xs text-center">
            Can't scan it? Enter this key instead:
            <br />
            <span className="font-mono text-white/70 break-all select-all">{setup.secret}</span>
          </p>
          <p className="text-white/50 text-sm">2. Enter the 6-digit code the app shows.</p>
          {codeInput}
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={reset} className="flex-1 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-sm h-12">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} className="flex-1 bg-white text-black hover:bg-gray-200 rounded-sm font-bold h-12" data-testid="two-factor-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Turn on"}
            </Button>
          </div>
        </form>
      )}

      {step === "codes" && (
        <div className="space-y-5">
          <p className="text-white/50 text-sm">
            Save these recovery codes somewhere safe. If you lose your phone, each one gets you in once.
            <strong className="text-white/80"> This is the only time they'll be shown.</strong>
          </p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-white/[0.05] border border-white/10 rounded-sm p-4" data-testid="recovery-codes">
            {recoveryCodes.map((c) => <span key={c}>{c}</span>)}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={copyCodes} className="flex-1 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-sm h-12">
              <Copy className="w-4 h-4 mr-2" />Copy
            </Button>
            <Button type="button" variant="outline" onClick={downloadCodes} className="flex-1 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-sm h-12">
              <Download className="w-4 h-4 mr-2" />Download
            </Button>
          </div>
          <Button onClick={() => { setRecoveryCodes([]); reset(); }} className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold h-12">
            I've saved them
          </Button>
        </div>
      )}

      {step === "regenerate" && (
        <form onSubmit={regenerate} className="space-y-5">
          <p className="text-white/50 text-sm">Your old recovery codes will stop working.</p>
          {codeInput}
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={reset} className="flex-1 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-sm h-12">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} className="flex-1 bg-white text-black hover:bg-gray-200 rounded-sm font-bold h-12">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Get new codes"}
            </Button>
          </div>
        </form>
      )}

      {step === "disable" && (
        <form onSubmit={disable} className="space-y-5">
          <div>
            <Label className="text-sm text-white/60">Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} required />
          </div>
          {codeInput}
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={reset} className="flex-1 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-sm h-12">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} className="flex-1 bg-white text-black hover:bg-gray-200 rounded-sm font-bold h-12">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Turn off"}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
